import { useCallback, useEffect, useRef, useState } from "react";

interface CaptionHistory {
  caption: string;
  history: string[];
  historyIndex: number;
  canUndo: boolean;
  canRedo: boolean;
  handleCaptionChange: (newCaption: string, isUserInput?: boolean) => void;
  handleUndo: () => void;
  handleRedo: () => void;
  resetHistory: (initialCaption?: string) => void;
  insertEmoji: (emoji: string, cursorPosition: number | null) => void; // Added cursorPosition
}

/**
 * Custom hook to manage caption text with undo/redo history.
 * @param initialCaption The initial caption string.
 * @param debounceTimeout The timeout in milliseconds for debouncing history updates. Defaults to 500ms.
 * @returns An object containing caption state and history management functions.
 */
export const useCaptionHistory = (
  initialCaption: string = "",
  debounceTimeout: number = 500
): CaptionHistory => {
  const [caption, setCaption] = useState<string>(initialCaption);
  const [history, setHistory] = useState<string[]>([initialCaption]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);

  // Ref to track if the caption change was triggered programmatically (e.g., by AI, undo/redo)
  // This helps prevent adding intermediate programmatic states to the history immediately after user input.
  const isProgrammaticChange = useRef(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Clears any existing debounce timer.
  const clearDebounceTimer = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  };

  const updateHistory = useCallback(
    (newCaption: string) => {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newCaption);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [history, historyIndex]
  );

  // This effect handles adding user input changes to the history after a debounce period.
  useEffect(() => {
    // Don't add initial state or programmatic changes immediately via this effect.
    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false; // Reset flag after handling
      return;
    }

    // Set a timer to add the current caption to history after a delay.
    // This prevents saving every single keystroke, only saving after the user pauses typing.
    // The .5 second wait allows for a natural pause in typing before committing to history.
    clearDebounceTimer();
    debounceTimer.current = setTimeout(() => {
      // Check if the current caption is different from the last saved state
      if (caption !== history[historyIndex]) {
        updateHistory(caption);
      }
    }, debounceTimeout);

    // Cleanup function to clear the timer if the component unmounts or caption changes again quickly.
    return () => {
      clearDebounceTimer();
    };
    // Dependency array includes caption and the updateHistory function.
  }, [caption, history, historyIndex, updateHistory, debounceTimeout]);

  const handleCaptionChange = (
    newCaption: string,
    isUserInput: boolean = true
  ) => {
    setCaption(newCaption);
    clearDebounceTimer(); // Clear any pending history update

    if (!isUserInput) {
      // If the change is programmatic (AI, undo/redo), update history immediately.
      isProgrammaticChange.current = true; // Set flag
      updateHistory(newCaption);
    }
    // User input changes are handled by the useEffect debounce logic.
  };

  const handleUndo = useCallback(() => {
    if (canUndo) {
      clearDebounceTimer(); // Prevent debounced update after undo/redo
      isProgrammaticChange.current = true; // Mark as programmatic change
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setCaption(history[prevIndex]);
    }
  }, [canUndo, history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (canRedo) {
      clearDebounceTimer(); // Prevent debounced update after undo/redo
      isProgrammaticChange.current = true; // Mark as programmatic change
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setCaption(history[nextIndex]);
    }
  }, [canRedo, history, historyIndex]);

  // Effect to handle global keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const isUndo =
        event.key === "z" &&
        (isMac ? event.metaKey : event.ctrlKey) &&
        !event.shiftKey;
      const isRedo =
        event.key === "z" &&
        (isMac ? event.metaKey : event.ctrlKey) &&
        event.shiftKey;

      if (isUndo) {
        event.preventDefault();
        handleUndo();
      } else if (isRedo) {
        event.preventDefault();
        handleRedo();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo, handleRedo]); // Add handleUndo and handleRedo as dependencies

  const resetHistory = (newInitialCaption: string = "") => {
    clearDebounceTimer();
    isProgrammaticChange.current = true; // Mark as programmatic change
    setCaption(newInitialCaption);
    setHistory([newInitialCaption]);
    setHistoryIndex(0);
  };

  const insertEmoji = useCallback(
    (emoji: string, cursorPosition: number | null) => {
      clearDebounceTimer(); // Clear any pending history update
      isProgrammaticChange.current = true; // Treat emoji insertion like programmatic change for history

      setCaption((prevCaption) => {
        const start = prevCaption.substring(
          0,
          cursorPosition ?? prevCaption.length
        );
        const end = prevCaption.substring(cursorPosition ?? prevCaption.length);
        const newCaption = `${start}${emoji}${end}`;
        updateHistory(newCaption); // Update history immediately after insertion
        return newCaption;
      });
    },
    [updateHistory]
  );

  return {
    caption,
    history,
    historyIndex,
    canUndo,
    canRedo,
    handleCaptionChange,
    handleUndo,
    handleRedo,
    resetHistory,
    insertEmoji,
  };
};
